import * as React from "react";
import { css } from "@emotion/core";
import {
  useLocation,
  useParams,
  useNavigate,
  Outlet,
  useMatch,
} from "react-router-dom";
import {
  useSinkState,
  useSinkActions,
  DrawingProvider,
  MapProvider,
  AnnotationProvider,
} from "context/";
import { useSupportState, useSupportActions } from "context/support";

import CenterPane from "./CenterPane";
import RightPane from "./RightPane";
import MapPane from "./Map/MapPane";
import Footer from "./Footer";
import ErrorBoundary from "./ErrorBoundary";
import Support from "components/User/Support";
import Loading from "../common/Loading";

import "@blueprintjs/datetime/lib/css/blueprint-datetime.css";
import "@blueprintjs/select/lib/css/blueprint-select.css";
import "@blueprintjs/table/lib/css/table.css";
import "mapbox-gl/dist/mapbox-gl.css";

const Portal = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const matchViewSurvey = useMatch("/surveys/:id");

  const {
    surveys,
    assets,
    selectedModelId,
    inspectorModel,
    selectedSurveyId,
    surveyListFilters,
    undoArchiveQueue,
    sink: { models, orders, organization },
    ...state
  } = useSinkState();
  const { setSelectedSurveyId, unsetSelectedSurvey } = useSinkActions();
  const selectedSurvey = React.useMemo(
    () => surveys.find((survey) => survey.id === Number(params.id)),
    [surveys, params.id]
  );
  const { isOpen: supportIsOpen } = useSupportState();
  const { open: openSupport } = useSupportActions();

  React.useEffect(() => {
    if (location.pathname.startsWith("/surveys") && params.id) {
      if (selectedSurvey) {
        setSelectedSurveyId(selectedSurvey.id);
      } else {
        navigate("/surveys", { replace: true });
      }
    } else {
      unsetSelectedSurvey();
    }
  }, [
    params.id,
    location.pathname,
    selectedSurvey,
    navigate,
    setSelectedSurveyId,
    unsetSelectedSurvey,
  ]);

  /*
    Filtering out archived entities is done at the top level under the assumption that
    archived entities should be treated (almost) exactly like truly deleted entities;
    exceptions (e.g. "undo delete") are just that-- exceptions. For the most part,
    the app will treat anything with `archived` set to `true` as though it doesn't exist.

    If that changes, it may be prudent to revisit the decision to filter all the way at the
    top level like this.
  */
  const layers = state.layers.filter((layer) => !layer.archived);
  const annotations = state.annotations.filter(
    (annotation) => !annotation.archived
  );

  const selectedModel =
    (selectedModelId && models.find((m) => m.id === selectedModelId)) || null;

  React.useEffect(() => {
    if (supportIsOpen === false && location.pathname.startsWith("/support")) {
      openSupport();
    }
  }, [location, openSupport]);

  if (location.pathname.startsWith("/surveys") && params.id) {
    if (!selectedSurvey) return <Loading />;
  }

  return (
    <>
      <section
        css={css`
          flex-grow: 1;
          position: relative;
          overflow: hidden;
          display: flex;
        `}
      >
        <MapProvider defaultStyle={organization.basemap}>
          <AnnotationProvider annotations={annotations}>
            <DrawingProvider surveyId={selectedSurveyId}>
              <aside
                css={css`
                  width: 430px;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Outlet />
              </aside>
              <main
                css={css`
                  flex-grow: 1;
                  position: relative;
                  display: ${selectedSurveyId ? "block" : "none"};
                `}
              >
                <ErrorBoundary>
                  <CenterPane selectedModel={selectedModel} />
                  <MapPane
                    surveys={surveys}
                    layers={layers}
                    orders={orders}
                    assets={assets}
                  />
                </ErrorBoundary>
              </main>
              {matchViewSurvey && (
                <aside
                  css={css`
                    position: absolute;
                    top: 0;
                    right: 0;
                    height: 100%;
                  `}
                >
                  <RightPane model={inspectorModel} />
                </aside>
              )}
            </DrawingProvider>
          </AnnotationProvider>
        </MapProvider>
      </section>
      <Footer
        survey={surveys.find((survey: any) => survey.id === selectedSurveyId)}
        orders={orders}
      />
      <Support />
    </>
  );
};

export default Portal;
